import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { adminApi, ClientverseHistoryFieldChange, ClientverseHistoryObjectChange, ClientverseHistoryVersion } from '../api/admin';

const HISTORY_PATH = '/admin/modules/data-mapping/clientverse/history';

function useClientverseHistory() {
  const { getToken } = useAuth();
  const [versions, setVersions] = useState<ClientverseHistoryVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const t = await getToken();
        const data = await adminApi.getClientverseHistory(t);
        setVersions(data.versions);
      } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    })();
    /* eslint-disable-line */
  }, []);

  return { versions, error };
}

// ClientVerse Core change history — list view. One row per known version,
// oldest first, with counts only; click a row for the full breakdown. Built
// entirely from the first_version_id/last_version_id stamps
// mi-job-salesforce-schema already maintains; see GET /admin/clientverse/history.
export default function AdminClientverseHistory() {
  const { versions, error } = useClientverseHistory();
  const navigate = useNavigate();

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <Link to="/admin/modules" style={breadcrumb}>Modules</Link>
        <span style={{ color: 'var(--mi-muted)' }}>›</span>
        <Link to="/admin/modules/data-mapping" style={breadcrumb}>Data Mapping</Link>
        <span style={{ color: 'var(--mi-muted)' }}>›</span>
        <h1 style={{ color: 'var(--mi-navy)', margin: 0 }}>ClientVerse Core Change History</h1>
      </div>
      <p style={{ color: 'var(--mi-muted)', marginTop: 4 }}>
        Every ClientVerse Core version this system has observed, oldest first. Click a version to see exactly which objects and fields were introduced or deprecated. The very first version has nothing to compare against, so it only shows its starting baseline.
      </p>

      {error && <div style={errBox}>{error}</div>}
      {versions === null && !error && <div style={{ color: 'var(--mi-muted)', fontSize: 13 }}>Loading…</div>}
      {versions && versions.length === 0 && (
        <div style={{ color: 'var(--mi-muted)', fontSize: 13, padding: 8 }}>No ClientVerse Core versions have been synced yet.</div>
      )}

      {versions && versions.length > 0 && (
        <section className="card">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={th}>Version</th>
                <th style={th}>Date</th>
                <th style={th}>Total Objects/Fields</th>
                <th style={th}>New Objects/Fields</th>
                <th style={th}>Deprecated Objects/Fields</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr
                  key={v.id}
                  onClick={() => navigate(`${HISTORY_PATH}/${v.id}`)}
                  style={{ borderTop: '1px solid var(--mi-border)', cursor: 'pointer' }}
                >
                  <td style={td}>
                    <Link to={`${HISTORY_PATH}/${v.id}`} onClick={(e) => e.stopPropagation()} style={versionLink}>
                      v{v.version_string}
                    </Link>
                    {v.is_baseline && <span style={{ ...baselineTag, marginLeft: 8 }}>baseline</span>}
                  </td>
                  <td style={td}>{new Date(v.first_synced_at).toLocaleDateString()}</td>
                  <td style={td}>{formatPair(v.total_objects, v.total_fields)}</td>
                  <td style={countPairCell(v.new_objects.length, v.new_fields.length, 'new')}>
                    {formatPair(v.new_objects.length, v.new_fields.length)}
                  </td>
                  <td style={countPairCell(v.deprecated_objects.length, v.deprecated_fields.length, 'deprecated')}>
                    {formatPair(v.deprecated_objects.length, v.deprecated_fields.length)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

// Detail view for one version — the full object/field breakdown, with a link
// back to the list.
export function AdminClientverseHistoryDetail() {
  const { versionId } = useParams<{ versionId: string }>();
  const { versions, error } = useClientverseHistory();
  const version = versions?.find((v) => String(v.id) === versionId) ?? null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <Link to="/admin/modules" style={breadcrumb}>Modules</Link>
        <span style={{ color: 'var(--mi-muted)' }}>›</span>
        <Link to="/admin/modules/data-mapping" style={breadcrumb}>Data Mapping</Link>
        <span style={{ color: 'var(--mi-muted)' }}>›</span>
        <Link to={HISTORY_PATH} style={breadcrumb}>Change History</Link>
        <span style={{ color: 'var(--mi-muted)' }}>›</span>
        <h1 style={{ color: 'var(--mi-navy)', margin: 0, fontFamily: 'monospace' }}>
          {version ? `v${version.version_string}` : '…'}
        </h1>
      </div>

      <div style={{ margin: '12px 0' }}>
        <Link to={HISTORY_PATH} style={{ ...outlineBtn, textDecoration: 'none' }}>← Back to Change History</Link>
      </div>

      {error && <div style={errBox}>{error}</div>}
      {versions === null && !error && <div style={{ color: 'var(--mi-muted)', fontSize: 13 }}>Loading…</div>}
      {versions !== null && !version && !error && (
        <div style={{ color: 'var(--mi-muted)', fontSize: 13, padding: 8 }}>No version found with that id.</div>
      )}

      {version && <VersionSection version={version} />}
    </div>
  );
}

function VersionSection({ version }: { version: ClientverseHistoryVersion }) {
  const hasNew = version.new_objects.length > 0 || version.new_fields.length > 0;
  const hasDeprecated = version.deprecated_objects.length > 0 || version.deprecated_fields.length > 0;

  return (
    <section className="card" style={{ borderTop: '3px solid var(--mi-purple)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        {version.is_baseline && <span style={baselineTag}>baseline</span>}
        <span style={{ fontSize: 12, color: 'var(--mi-muted)' }}>
          first synced {new Date(version.first_synced_at).toLocaleDateString()}
        </span>
      </div>

      {!hasNew && !hasDeprecated && (
        <div style={{ fontSize: 13, color: 'var(--mi-muted)', padding: '8px 0' }}>No object or field changes detected.</div>
      )}

      {version.new_objects.length > 0 && (
        <ChangeTable title="New objects" tone="new" rows={version.new_objects.map((o) => ({ primary: o.name, secondary: o.label }))} />
      )}
      {version.new_fields.length > 0 && (
        <ChangeTable
          title="New fields"
          tone="new"
          rows={version.new_fields.map((f) => ({ primary: `${f.object_name}.${f.name}`, secondary: f.label }))}
        />
      )}
      {version.deprecated_objects.length > 0 && (
        <ChangeTable
          title="Deprecated objects"
          tone="deprecated"
          rows={version.deprecated_objects.map((o: ClientverseHistoryObjectChange) => ({ primary: o.name, secondary: o.label }))}
        />
      )}
      {version.deprecated_fields.length > 0 && (
        <ChangeTable
          title="Deprecated fields"
          tone="deprecated"
          rows={version.deprecated_fields.map((f: ClientverseHistoryFieldChange) => ({ primary: `${f.object_name}.${f.name}`, secondary: f.label }))}
        />
      )}
    </section>
  );
}

function ChangeTable({ title, tone, rows }: {
  title: string;
  tone: 'new' | 'deprecated';
  rows: Array<{ primary: string; secondary: string | null }>;
}) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={tone === 'new' ? newTag : deprecatedTag}>{tone === 'new' ? 'NEW' : 'DEPRECATED'}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--mi-navy)' }}>{title}</span>
        <span style={{ fontSize: 12, color: 'var(--mi-muted)' }}>({rows.length})</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <tbody>
          {rows.map((row) => (
            <tr key={row.primary} style={{ borderTop: '1px solid var(--mi-border)' }}>
              <td style={{ ...td, fontFamily: 'monospace', width: '45%' }}>{row.primary}</td>
              <td style={{ ...td, color: 'var(--mi-muted)' }}>{row.secondary && row.secondary !== row.primary ? row.secondary : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Styles
// -----------------------------------------------------------------------------
const breadcrumb: React.CSSProperties = { color: 'var(--mi-muted)', fontSize: 14, textDecoration: 'none' };
const errBox: React.CSSProperties = { background: '#fee', color: '#900', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 14 };
const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700,
  color: 'var(--mi-navy)', textTransform: 'uppercase', letterSpacing: 0.5,
  borderBottom: '1px solid var(--mi-border)', whiteSpace: 'nowrap',
};
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' };
const versionLink: React.CSSProperties = { fontFamily: 'monospace', fontWeight: 600, color: 'var(--mi-navy)' };
const outlineBtn: React.CSSProperties = {
  background: 'transparent', border: '1px solid var(--mi-border)',
  color: 'var(--mi-navy)', padding: '4px 10px', borderRadius: 6,
  cursor: 'pointer', fontSize: 12, fontWeight: 500,
};
const baselineTag: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
  color: 'var(--mi-muted)', border: '1px solid var(--mi-border)', borderRadius: 999, padding: '2px 8px',
};
const newTag: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: 0.4, color: '#1a7a3a',
  background: '#e6f6ec', border: '1px solid #b8e6c8', borderRadius: 999, padding: '2px 8px',
};
const deprecatedTag: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: 0.4, color: '#a11414',
  background: '#fdecec', border: '1px solid #f3c0c0', borderRadius: 999, padding: '2px 8px',
};
// Objects/Fields counts share one column, e.g. "51 / 1,202" — order matches
// the column header's "Objects/Fields" naming.
function formatPair(objectCount: number, fieldCount: number): string {
  return `${objectCount.toLocaleString()} / ${fieldCount.toLocaleString()}`;
}
function countPairCell(objectCount: number, fieldCount: number, tone: 'new' | 'deprecated'): React.CSSProperties {
  if (objectCount === 0 && fieldCount === 0) return { ...td, color: 'var(--mi-muted)' };
  return { ...td, fontWeight: 600, color: tone === 'new' ? '#1a7a3a' : '#a11414' };
}
